import React from 'react';
import { FaMicroscope, FaNotesMedical, FaPlus } from 'react-icons/fa6';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useFetchProjectQuery } from '../api/project';
import Breadcrumbs from '../Components/Breadcrumbs/Breadcrumbs';
import ImageActionButtons from '../Components/Image/ImageActionButtons';
import ImageList from '../Components/Image/ImageList';
import MultipleImageUpload from '../Components/Image/MultipleImageUpload';
import LoadingSpiner from '../Components/Loader/LoadingSpiner';
import ProjectActionButtons from '../Components/Project/ProjectActionButtons';
import ProjectTitleEditor from '../Components/Project/ProjectTitleEditor';
import ActionCard from '../Layouts/ActionCard';
import ContentCard from '../Layouts/ContentCard';
import { selectImageIdsState } from '../Store/imageSlice';
import { openModal } from '../Store/modalSlice';
import { RootState } from '../Store/store';
import { useImageList } from '../Components/Image/hooks/useImageList';
import StarToggle from '../Components/Favorite/StarToggle';

export default function ProjectPage() {
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const { data, isLoading: isProjectLoading, isError } = useFetchProjectQuery(projectId);
  
  // Use the hook to get image count for conditional rendering
  const { serverImageCount, isLoading: isImagesLoading } = useImageList({
    projectId: projectId || '',
    autoFetchAll: false
  });
  
  const selectedImageIds = useSelector(selectImageIdsState);
  // Keep the inline uploader mounted while a batch is running — the list now
  // refreshes itself, so the first row landing would otherwise rip the queue UI
  // out from under the remaining files. The empty-state test below deliberately
  // uses serverImageCount: pending stand-in rows are not images the project
  // actually has yet, and counting them would unmount the uploader (and its
  // "Clear Completed" button) the instant the last file finished.
  const uploadQueue = useSelector((state: RootState) => state.imageQueue);
  const isUploading =
    uploadQueue.queue.some((img) => img.projectId === projectId) ||
    uploadQueue.inprog.some((img) => img.projectId === projectId);

  if (isProjectLoading || isImagesLoading)
    return (
      <div className="">
        <LoadingSpiner />
      </div>
    );
  if (isError) return <div>Error fetching project</div>;

  const { name, description } = data;

  const handleUploadClick = () => {
    dispatch(openModal({ type: "image", width: "lg", props: { projectId: projectId } }));
  };

  const handleAnalyzeClick = () => {
    dispatch(openModal({ type: "analyze", width: "lg", props: { projectId: projectId } }));
  };

  const handleCreateReportClick = () => {
    dispatch(openModal({ type: "report", width: "full", props: { projectId: projectId } }));
  };

  const mockCollaborators = [
    // { name: 'Johnathan', initial: 'J' },
    // { name: 'George', initial: 'G' },
    // { name: 'Charlie', initial: 'C' }
  ] as { name: string; initial: string }[];

  return (
    <div className="container mx-auto">
      <div className="mt-4 grid grid-cols-1 gap-4">
        <div className="flex items-center py-2">
          <ProjectTitleEditor key={projectId} name={name} id={`${projectId}`} />
          <StarToggle entityId={projectId || ''} entityType="Project" className="ml-2" />
          <div className="flex items-center">
            {mockCollaborators.map(collaborator => {
              const color = `hsl(${collaborator.initial.charCodeAt(0) * 10 % 360}, 70%, 50%)`;
              return (
                <div key={collaborator.name} className="flex items-center justify-center w-8 h-8 rounded-full text-white ml-2" style={{ backgroundColor: color, border: '2px solid white', boxShadow: '0 0 5px rgba(0, 0, 0, 0.2)' }} title={collaborator.name}>
                  <div className="tooltip tooltip-bottom" data-tip={collaborator.name}>
                    <div className="avatar offline placeholder cursor-pointer">
                      <div className="bg-neutral-focus text-neutral-content rounded-full w-12">
                        <span>{collaborator.initial}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <p className="text-gray-600">{description}</p>
      </div>
      {/* Create a 5 column container */}
      <div className="mt-4 grid h-28 grid-cols-8 gap-4">
        <ActionCard title="Upload Image" icon={<FaPlus />} onClick={handleUploadClick}></ActionCard>
        <ActionCard className="bg-neutral-300 dark:bg-neutral-800" title="Analyze" icon={<FaMicroscope />} onClick={handleAnalyzeClick}></ActionCard>
        <ActionCard
          className="bg-neutral-300 dark:bg-neutral-800"
          title="Create Report"
          icon={<FaNotesMedical />}
          onClick={handleCreateReportClick}
        ></ActionCard>
      </div>
      {selectedImageIds.length === 0 && <Breadcrumbs />}
      {selectedImageIds.length > 0 && <ImageActionButtons projectId={projectId || ""} />}
      <ContentCard className="mx-0 pb-24" title="">
        <ImageList key={projectId} projectId={projectId || ""} />
      </ContentCard>
      {(!serverImageCount || isUploading) && (
        <ContentCard className="mx-0 pb-24" title="">
          <MultipleImageUpload projectId={`${projectId}`} />
        </ContentCard>
      )}
    </div>
  );
}
